import { ReportElement } from './report-element';
import { ReportUnits } from './report-units';
import { ElementLocationInSection, Options, TemplateDropDownList } from './report-template';
import { ElementType } from './element-type';
import { ReportPreviewMetadata, PropertyPreviewMetadata, FieldType, UpdateMetadata } from '../_services/report-template-design.service';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { LangUpdateService } from '../_services/lang-update.service';
import { BORDER_STYLE } from '../app.constants';

export class ReportDropDownList extends ReportElement {
  public top: string;
  public left: string;
  public zIndex: number;
  public width: string;
  public height: string;
  public borderWidth: string;
  public borderColor: string;
  public borderStyle: string;
  public selectedValue: string;
  public options: Options[];
  public isCustomElementDraw?:boolean;

  private units: ReportUnits;

  private baseDef: TemplateDropDownList;
  private readonly metaId = 'id';
  private readonly metaTop = 'y';
  private readonly metaLeft = 'x';
  private readonly metaWidth = 'width';
  private readonly metaHeight = 'height';
  private readonly metaZIndex = 'zIndex';
  private readonly metaBorderWidth = 'borderWidth';
  private readonly metaBorderStyle = 'borderStyle';
  private readonly metaBorderColor = 'borderColor';
  private readonly metaOptions = 'options';

  constructor(index: number, units: ReportUnits, 
    protected translate: TranslateService,
    protected langService: LangUpdateService,
    templateDropDownList?: TemplateDropDownList,
) {

    super(ElementType.DropDownList, index, !(templateDropDownList == null), translate, langService, templateDropDownList ? templateDropDownList.id : null);

    this.units = units;

    if (templateDropDownList) {
      this.baseDef = templateDropDownList;
      this.baseDef.id = this.baseDef.id ? this.baseDef.id : this.generateUUID();
    } else {
      this.baseDef = {
        id: this.generateUUID(),
        x: null,
        y: null,
        width: null,
        height: null,
        zIndex: null,
        borderStyle: null,
        borderColor: null,
        borderWidth: null,
        selectedValue: null,
        options: []
      };
    }

    this.zIndex = this.baseDef.zIndex;
    this.top = this.units.concatenate('sizeUnit', this.baseDef.y);
    this.left = this.units.concatenate('sizeUnit', this.baseDef.x);
    this.width = this.units.concatenate('sizeUnit', this.baseDef.width);
    this.height = this.units.concatenate('sizeUnit', this.baseDef.height);;
    this.borderWidth = this.units.concatenate('sizeUnit', this.baseDef.borderWidth);
    this.borderColor = this.baseDef.borderColor;
    this.borderStyle = this.baseDef.borderStyle;
    this.options = this.baseDef[this.metaOptions];

    this.setChainUpdate(); 
    this.setPreviewMetadata();
  }

  public setDrawData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTop]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaWidth,
      value: this.units.fromPx('sizeUnit', metadata.widthPx),
      oldValue: this.baseDef[this.metaWidth]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaHeight,
      value: this.units.fromPx('sizeUnit', metadata.heightPx),
      oldValue: this.baseDef[this.metaHeight]
    });
  }

  public setDropData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTop]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft]
    });
  }

  public setResizeData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTop]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaWidth,
      value: this.units.fromPx('sizeUnit', metadata.widthPx),
      oldValue: this.baseDef[this.metaWidth]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaHeight,
      value: this.units.fromPx('sizeUnit', metadata.heightPx),
      oldValue: this.baseDef[this.metaHeight]
    });
  }

  public getLeftPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaLeft]);
  }
  public getTopPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaTop]);
  }
  public getWidthPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaWidth]);
  }
  public getHeightPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaHeight]);
  }

  public getTemplateJSON(): TemplateDropDownList {
    return this.baseDef;
  }

  protected getPreviewMetadata(): PropertyPreviewMetadata[] {
    return [
      {
        name: this.metaLeft, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Position'
      },
      {
        name: this.metaTop, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Position'
      },
      {
        name: this.metaZIndex, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Integer',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'Position'
      },
      {
        name: this.metaHeight, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Size'
      },
      {
        name: this.metaWidth, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Size'
      },
      {
        name: this.metaBorderWidth, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'Line'
      },
      {
        name: this.metaBorderColor, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Color',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'Line'
      },
      {
        name: this.metaBorderStyle, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: BORDER_STYLE,
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'Line'
      },
      {
        name: this.metaOptions, parentName: null, value: this.baseDef.options,
        isFieldType: (type: FieldType): boolean => type === 'DropDownOptionItems',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'DataBinding'
      }
    ];
  }

  public setSectionInfo(id: string, stageName: string, location: ElementLocationInSection): void {
    this.baseDef.sectionId = id;
    this.baseDef.sectionStageTag = stageName;
    this.baseDef.elementLocationInSection = location;
  }

  protected setUpdateMetadata(metadata: UpdateMetadata) {
    switch (metadata.name) {
      case this.metaId:
        this.baseDef[metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaTop:
        this.baseDef[metadata.name] = metadata.value as number;
        this.top = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaLeft:
        this.baseDef[metadata.name] = metadata.value as number;
        this.left = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaWidth:
        this.baseDef[metadata.name] = metadata.value as number;
        this.width = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaHeight:
        this.baseDef[metadata.name] = metadata.value as number;
        this.height = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaZIndex:
        this.baseDef[metadata.name] = metadata.value as number;
        this.zIndex = metadata.value as number;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaBorderStyle:
        this.baseDef[metadata.name] = metadata.value as string;
        this.borderStyle = metadata.value as string;
        break;
      case this.metaBorderColor:
        this.baseDef[metadata.name] = metadata.value as string;
        this.borderColor = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaBorderWidth:
        this.baseDef[metadata.name] = metadata.value as number;
        this.borderWidth = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaOptions: 
        this.baseDef[metadata.name] = metadata.value as Options[];
        this.options = metadata.value as Options[];
        this.setPreviewValue(metadata.name, metadata.value);
        break;
    }
  }
  protected setChainUpdateFunction(subscribers: Array<Subscription>) {
    subscribers.push(
      this.units.onUpdateSizeUnit.subscribe((metadata: UpdateMetadata) => {
        this.updateProperties({
          id: this.getId(),
          name: this.metaTop,
          value: this.convertUnit(this.baseDef[this.metaTop], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaTop]
        });
        this.updateProperties({
          id: this.getId(),
          name: this.metaLeft,
          value: this.convertUnit(this.baseDef[this.metaLeft], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaLeft]
        });
        this.updateProperties({
          id: this.getId(),
          name: this.metaWidth,
          value: this.convertUnit(this.baseDef[this.metaWidth], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaWidth]
        });
        this.updateProperties({
          id: this.getId(),
          name: this.metaHeight,
          value: this.convertUnit(this.baseDef[this.metaHeight], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaHeight]
        });
      })
    );
  }

  public isCustomElement() {
    return this.baseDef['isCustomElementDraw'];
   }
}
